import React from "react";
import "./IsfSiteOverviewPanel.scss";

function KpiTile({ label, value, sub, tone = "default" }) {
  return (
    <div className={`isf-site-overview__kpi isf-site-overview__kpi--${tone}`}>
      <div className="isf-site-overview__kpi-value">{value}</div>
      <div className="isf-site-overview__kpi-label">{label}</div>
      {sub ? <div className="isf-site-overview__kpi-sub">{sub}</div> : null}
    </div>
  );
}

function TaskRow({ task, onAction }) {
  const statusClass =
    task.status === "Overdue"
      ? "overdue"
      : task.type === "sign"
        ? "sign"
        : "pending";

  return (
    <div
      className={`isf-site-overview__task${task.canAct ? " is-actionable" : ""}`}
    >
      <div className="isf-site-overview__task-icon" aria-hidden="true">
        {task.type === "sign" ? (
          <i className="fas fa-file-signature" />
        ) : (
          <i className="fas fa-upload" />
        )}
      </div>
      <div className="isf-site-overview__task-body">
        <div className="isf-site-overview__task-title" title={task.title}>
          {task.title}
        </div>
        <div className="isf-site-overview__task-sub" title={task.subtitle}>
          {task.subtitle}
        </div>
      </div>
      <span
        className={`isf-site-overview__task-status isf-site-overview__task-status--${statusClass}`}
      >
        {task.status}
      </span>
      {task.canAct ? (
        <button
          type="button"
          className="isf-site-overview__task-btn"
          onClick={() => onAction?.(task)}
        >
          {task.actionLabel}
        </button>
      ) : null}
    </div>
  );
}

export default function IsfSiteOverviewPanel({
  siteName = "",
  studyName = "",
  snapshot = {},
  onTaskAction,
}) {
  const kpis = snapshot.kpis || {};
  const openItems = snapshot.tasks || [];
  const uploadLabel =
    kpis.uploadTotal > 0 ? `${kpis.uploadDone}/${kpis.uploadTotal}` : "—";
  const signLabel =
    kpis.signTotal > 0 ? `${kpis.signDone}/${kpis.signTotal}` : "—";

  return (
    <div className="isf-site-overview h-100">
      <div className="isf-site-overview__static">
        <nav
          className="isf-site-overview__crumb"
          aria-label="Site overview context"
        >
          <ol className="isf-site-overview__crumb-list">
            {studyName ? (
              <li className="isf-site-overview__crumb-item">
                <span className="isf-site-overview__crumb-text" title={studyName}>
                  {studyName}
                </span>
              </li>
            ) : null}
            {siteName ? (
              <li className="isf-site-overview__crumb-item">
                <span className="isf-site-overview__crumb-text" title={siteName}>
                  {siteName}
                </span>
              </li>
            ) : null}
            <li
              className="isf-site-overview__crumb-item is-active"
              aria-current="page"
            >
              Overview
            </li>
          </ol>
        </nav>

        <div className="isf-site-overview__kpis" data-tour="isf-site-overview-kpis">
          <KpiTile
            label="Uploads"
            value={uploadLabel}
            sub="Documents uploaded"
            tone="upload"
          />
          <KpiTile
            label="Signatures"
            value={signLabel}
            sub="Signer completions"
            tone="sign"
          />
          <KpiTile
            label="Overdue"
            value={kpis.overdueCount || 0}
            sub="Need attention"
            tone={kpis.overdueCount > 0 ? "warn" : "default"}
          />
          <KpiTile
            label="My tasks"
            value={kpis.myTasksCount || 0}
            sub="Assigned to you"
            tone="mine"
          />
        </div>
      </div>

      <div
        className="isf-site-overview__section isf-site-overview__section--list"
        data-tour="isf-site-open-tasks"
      >
        <div className="isf-site-overview__section-head">
          <h6>All open items</h6>
          <span>{openItems.length} pending</span>
        </div>
        {openItems.length ? (
          <div className="isf-site-overview__task-list isf-site-overview__task-list--scroll isf-scroll-light">
            {openItems.map((task) => (
              <TaskRow key={task.id} task={task} onAction={onTaskAction} />
            ))}
          </div>
        ) : (
          <div className="isf-site-overview__empty">
            <p>No pending uploads or signatures for this site.</p>
          </div>
        )}
      </div>
    </div>
  );
}
